import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";

import {
  CloseOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import {
  Button, Input, Layout, Select, Table, Tooltip,
} from "antd";
import moment from "moment";
import { Slide } from "react-awesome-reveal";
import { toast } from "react-toastify";

import DatePicker from "../../Common/DatePicker/DatePicker";
import Loading from "../../Common/Loading";
import PdfPreviewModal from "../../Common/UIComponents/PdfPreviewModal";
import {
  getSearchableSelectProps,
  renderSelectPopupScrollbar,
  selectPopupClassNames,
  selectSearchSuffixIcon,
} from "../../Common/UIComponents/SelectPopupScrollbar";
import StyledScrollbar from "../../Common/UIComponents/StyledScrollbar";
import StyledTooltip from "../../Common/UIComponents/StyledTooltip";
import MyAccountSideMenu from "../MyAccountSideMenu";
import CreateSubmissionInvoice from "./CreateSubmissionInvoice";
import InvoicePayment, { InvoicePaymentProvider } from "./InvoicePayment";

import { downloadFileFromS3WithPresigned } from "../../../utils/helpers";
import { canCreateSubmissionInvoice } from "../../../utils/Permissions";
import {
  formatInvoiceAmount,
  INVOICE_TYPE_LABELS,
  mapInvoicesToRows,
  resolveInvoiceTypeParam,
} from "./utils";

import { getInvoicePdfPresignedUrl, listInvoiceCreatedBy, listInvoices } from "../../../actions/invoice";

import { ReactComponent as CalendarLineIcon } from "../../../assets/images/subscriptions/calendar-line.svg";
import { ReactComponent as DownloadLineIcon } from "../../../assets/images/subscriptions/download-line.svg";
import { ReactComponent as PreviewLineIcon } from "../../../assets/images/subscriptions/preview-line.svg";
import { ReactComponent as ReceiptLineIcon } from "../../../assets/images/subscriptions/receipt-line-white.svg";
import { ReactComponent as SearchLineIcon } from "../../../assets/images/subscriptions/search-line.svg";

import "./Invoice.css";

const DEFAULT_PAGE_SIZE = 20;

const getDefaultDateRange = () => {
  const end = moment();
  const start = moment().subtract(1, "year");

  return {
    label: `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`,
    dateFrom: start.format("YYYY-MM-DD"),
    dateTo: end.format("YYYY-MM-DD"),
  };
};

const DEFAULT_DATE_RANGE = getDefaultDateRange();

const INVOICE_TYPE_FILTERS = [
  {
    key: "submission",
    label: "Submission",
    swatchClassName: "Invoice__TypeFilterSwatch--submission",
  },
  {
    key: "negative_credit",
    label: "Adjustment",
    swatchClassName: "Invoice__TypeFilterSwatch--negative_credit",
  },
  {
    key: "subscription",
    label: "Subscription",
    swatchClassName: "Invoice__TypeFilterSwatch--subscription",
  },
  {
    key: "topup",
    label: "Top-up",
    swatchClassName: "Invoice__TypeFilterSwatch--topup",
  },
  {
    key: "postpay",
    label: "Postpay",
    swatchClassName: "Invoice__TypeFilterSwatch--postpay",
  },
];

const formatInvoiceDate = (date) => moment(date).format("MMM DD, YYYY");

const getPdfFileName = (pdfPath) => pdfPath?.split("/").pop();

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function HighlightText({ text, query }) {
  const value = text == null ? "" : String(text);
  const term = query?.trim();
  if (!term) return value;

  const parts = value.split(new RegExp(`(${escapeRegExp(term)})`, "ig"));
  return parts.map((part, index) => (
    part.toLowerCase() === term.toLowerCase()
      ? <mark key={index} className="Invoice__Highlight">{part}</mark> // eslint-disable-line react/no-array-index-key
      : part
  ));
}

function InvoiceNameCell({ name, query }) {
  return (
    <StyledTooltip title={name} placement="top">
      <span className="Invoice__Name">
        <HighlightText text={name} query={query} />
      </span>
    </StyledTooltip>
  );
}

export default function Invoice({ history }) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateSelected, setDateSelected] = useState(DEFAULT_DATE_RANGE.label);
  const [prevDateSelected, setPrevDateSelected] = useState(DEFAULT_DATE_RANGE.label);
  const [dateFrom, setDateFrom] = useState(DEFAULT_DATE_RANGE.dateFrom);
  const [dateTo, setDateTo] = useState(DEFAULT_DATE_RANGE.dateTo);
  const [activeTypes, setActiveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [actionLoadingKey, setActionLoadingKey] = useState("");
  const [previewRecord, setPreviewRecord] = useState(null);
  const [listVersion, setListVersion] = useState(0);
  const [pageLoadFailed, setPageLoadFailed] = useState(false);
  const [createdBy, setCreatedBy] = useState("");
  const [createdBySearch, setCreatedBySearch] = useState("");
  const [creatorNames, setCreatorNames] = useState([]);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const scrollbarRef = useRef(null);
  const fillLockRef = useRef(false);
  const wasLoadingMoreRef = useRef(false);
  const appliedSearchRef = useRef("");
  const allowCreateSubmissionInvoice = canCreateSubmissionInvoice();

  const handleMenuClick = ({ key }) => {
    const routes = {
      1: "/myaccount/profile",
      2: "/myaccount/members",
      3: "/myaccount/usage-metrics",
      4: "/myaccount/invoice",
      5: "/myaccount/help",
    };

    if (routes[key]) {
      history.push(routes[key]);
    }
  };

  const resetAndReload = useCallback(() => {
    setPageLoadFailed(false);
    setHasMore(true);
    setLoading(true);
    setLoadingMore(false);
    setRecords([]);
    setPage(1);
    fillLockRef.current = false;
    if (scrollbarRef.current) {
      scrollbarRef.current.scrollToTop();
    }
  }, []);

  const applySearch = useCallback((value) => {
    const nextSearch = value.trim();
    if (appliedSearchRef.current === nextSearch) return;

    appliedSearchRef.current = nextSearch;
    setDebouncedSearch(nextSearch);
    resetAndReload();
  }, [resetAndReload]);

  useEffect(() => {
    const timer = setTimeout(() => applySearch(search), 350);

    return () => clearTimeout(timer);
  }, [applySearch, search]);

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;
    const isFirstPage = page === 1;

    const loadInvoices = async () => {
      setPageLoadFailed(false);
      if (isFirstPage) {
        setLoading(true);
        setLoadingMore(false);
      } else {
        setLoadingMore(true);
      }

      const response = await listInvoices({
        type: resolveInvoiceTypeParam(activeTypes),
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        search: debouncedSearch || undefined,
        createdBy: createdBy || undefined,
        page,
        pageSize: DEFAULT_PAGE_SIZE,
        signal: controller.signal,
      });

      if (!isMounted || response.canceled) return;

      if (!response.success) {
        toast.error(response.message || "Failed to load invoices");
        if (isFirstPage) {
          setRecords([]);
          setHasMore(false);
        } else {
          setPageLoadFailed(true);
        }
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      const nextRows = mapInvoicesToRows(response.invoices);
      setRecords((prev) => (isFirstPage ? nextRows : [...prev, ...nextRows]));
      setHasMore(nextRows.length > 0 && page * DEFAULT_PAGE_SIZE < response.count);
      setLoading(false);
      setLoadingMore(false);
    };

    loadInvoices();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [activeTypes, createdBy, dateFrom, dateTo, debouncedSearch, listVersion, page]);

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const loadCreators = async () => {
      const response = await listInvoiceCreatedBy(controller.signal);
      if (!isMounted || response.canceled) return;
      if (!response.success) {
        toast.error(response.message || "Failed to load created by");
        return;
      }
      setCreatorNames(response.names);
    };

    loadCreators();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  const handleDateFilter = (startDate, endDate) => {
    setDateFrom(startDate);
    setDateTo(endDate || startDate);
    resetAndReload();
  };

  const toggleTypeFilter = (typeKey) => {
    setActiveTypes((prev) => (
      prev.includes(typeKey)
        ? prev.filter((key) => key !== typeKey)
        : [...prev, typeKey]
    ));
    resetAndReload();
  };

  const handlePaymentSuccess = useCallback(() => {
    resetAndReload();
    setListVersion((version) => version + 1);
  }, [resetAndReload]);

  const handleCreateInvoiceExit = useCallback(() => {
    setCreatingInvoice(false);
    handlePaymentSuccess();
  }, [handlePaymentSuccess]);

  /**
   * On scroll stop, if scroll bar is at the bottom, load the next page of rows
   * @param {Object} e on scroll stop event values from StyledScrollbar
   */
  const handleScrollToBottom = (e) => {
    if (!e || loading || loadingMore || !hasMore) return;

    const tolerance = 3;
    const bottom = Math.abs(
      Math.ceil(e.contentScrollHeight - e.scrollTop) - Math.ceil(e.clientHeight),
    ) <= tolerance;

    if (!bottom) return;

    if (pageLoadFailed) {
      setListVersion((version) => version + 1);
      return;
    }

    setPage((prev) => prev + 1);
  };

  useEffect(() => {
    if (wasLoadingMoreRef.current && !loading && !loadingMore) {
      fillLockRef.current = false;
    }
    wasLoadingMoreRef.current = loadingMore;
  }, [loading, loadingMore]);

  useEffect(() => {
    if (loading || loadingMore || !hasMore || pageLoadFailed || records.length === 0) {
      return undefined;
    }

    let cancelled = false;
    const loadIfShort = () => {
      if (cancelled || fillLockRef.current) return;
      const scrollbar = scrollbarRef.current;
      if (!scrollbar || typeof scrollbar.getScrollState !== "function") return;

      const { clientHeight, scrollYPossible } = scrollbar.getScrollState(true);
      if (clientHeight <= 0 || scrollYPossible) return;

      fillLockRef.current = true;
      setPage((current) => current + 1);
    };

    const frame = requestAnimationFrame(loadIfShort);
    const holder = scrollbarRef.current?.holderElement;
    const observer = typeof ResizeObserver !== "undefined" && holder
      ? new ResizeObserver(loadIfShort)
      : null;
    if (observer) observer.observe(holder);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
    };
  }, [hasMore, loading, loadingMore, pageLoadFailed, records.length]);

  const resolvePdfUrl = useCallback(async (record, signal) => {
    const response = await getInvoicePdfPresignedUrl(record.pdfPath, signal);
    if (response.canceled) return null;

    if (!response.success) {
      toast.error("Failed to load invoice preview");
      return null;
    }

    return response.url;
  }, []);

  const actionLoadingKeyRef = useRef("");

  const handleViewInvoice = useCallback(async (record) => {
    if (record.pdfPath == null || actionLoadingKeyRef.current) return;

    const actionKey = `${record.id}-view`;
    actionLoadingKeyRef.current = actionKey;
    setActionLoadingKey(actionKey);

    try {
      const url = await resolvePdfUrl(record);
      if (actionLoadingKeyRef.current !== actionKey || !url) return;
      setPreviewRecord({ ...record, resolvedUrl: url });
    } finally {
      if (actionLoadingKeyRef.current === actionKey) {
        actionLoadingKeyRef.current = "";
        setActionLoadingKey("");
      }
    }
  }, [resolvePdfUrl]);

  const previewFiles = useMemo(() => {
    if (!previewRecord?.resolvedUrl) return [];

    return [{
      key: previewRecord.id,
      fileName: getPdfFileName(previewRecord.pdfPath),
      src: previewRecord.resolvedUrl,
    }];
  }, [previewRecord]);

  const handleDownloadInvoice = useCallback(async (record) => {
    if (record.pdfPath == null) return;

    const actionKey = `${record.id}-download`;
    if (actionLoadingKeyRef.current) return;
    actionLoadingKeyRef.current = actionKey;
    setActionLoadingKey(actionKey);

    try {
      const success = await downloadFileFromS3WithPresigned(
        [record.pdfPath],
        "private",
        [getPdfFileName(record.pdfPath)],
      );
      if (!success) {
        toast.error("Failed to download invoice");
      }
    } finally {
      if (actionLoadingKeyRef.current === actionKey) {
        actionLoadingKeyRef.current = "";
        setActionLoadingKey("");
      }
    }
  }, []);

  const createdByQuery = createdBy || createdBySearch;

  const columns = useMemo(() => [
    {
      title: "Invoice Name",
      dataIndex: "name",
      key: "name",
      width: "13%",
      ellipsis: true,
      render: (name) => <InvoiceNameCell name={name} query={debouncedSearch} />,
    },
    {
      title: "Invoice ID",
      dataIndex: "id",
      key: "id",
      width: "12%",
      render: (id) => <HighlightText text={id} query={debouncedSearch} />,
    },
    {
      title: "Invoice Date",
      dataIndex: "date",
      key: "date",
      width: "11%",
      render: (date) => formatInvoiceDate(date),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: "10%",
      render: (type) => (
        <span className={`Invoice__TypeBadge Invoice__TypeBadge--${type}`}>
          {INVOICE_TYPE_LABELS[type]}
        </span>
      ),
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      key: "createdBy",
      width: "12%",
      ellipsis: true,
      render: (name) => <InvoiceNameCell name={name} query={createdByQuery} />,
    },
    {
      title: "Bill To",
      dataIndex: "billTo",
      key: "billTo",
      width: "12%",
      ellipsis: true,
      render: (billTo) => <InvoiceNameCell name={billTo} />,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: "9%",
      align: "right",
      render: (amount, record) => (
        <span className="Invoice__Amount">
          {formatInvoiceAmount(amount, record.currency)}
        </span>
      ),
    },
    {
      title: "Payment Status",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      width: "13%",
      render: (status, record) => (
        <InvoicePayment record={record} status={status} />
      ),
    },
    {
      title: "",
      key: "actions",
      width: "8%",
      align: "center",
      render: (_, record) => {
        const downloadLoading = actionLoadingKey === `${record.id}-download`;
        const viewLoading = actionLoadingKey === `${record.id}-view`;
        const busy = Boolean(actionLoadingKey);
        const isGenerating = record.pdfPath == null;

        if (isGenerating) {
          return null;
        }

        return (
          <div className="Invoice__Actions">
            <Tooltip title="View invoice">
              <button
                type="button"
                className="Invoice__ActionButton"
                aria-label={`View ${record.name}`}
                disabled={busy}
                onClick={() => handleViewInvoice(record)}
              >
                {viewLoading ? <LoadingOutlined /> : <PreviewLineIcon aria-hidden />}
              </button>
            </Tooltip>
            <Tooltip title="Download invoice">
              <button
                type="button"
                className="Invoice__ActionButton"
                aria-label={`Download ${record.name}`}
                disabled={busy}
                onClick={() => handleDownloadInvoice(record)}
              >
                {downloadLoading ? (
                  <LoadingOutlined />
                ) : (
                  <DownloadLineIcon aria-hidden />
                )}
              </button>
            </Tooltip>
          </div>
        );
      },
    },
  ], [
    actionLoadingKey,
    createdByQuery,
    debouncedSearch,
    handleDownloadInvoice,
    handleViewInvoice,
  ]);

  const creatorOptions = useMemo(() => (
    creatorNames.map((name) => ({ value: name, label: name }))
  ), [creatorNames]);

  if (creatingInvoice && allowCreateSubmissionInvoice) {
    return (
      <InvoicePaymentProvider onPaid={handlePaymentSuccess}>
        <div className="myAccountMainContainer">
          <Layout className="Invoice__Layout">
            <MyAccountSideMenu
              selectedMenuItem="4"
              handleMenuClick={handleMenuClick}
            />
            <main className="Invoice__Content">
              <CreateSubmissionInvoice onBack={handleCreateInvoiceExit} />
            </main>
          </Layout>
        </div>
      </InvoicePaymentProvider>
    );
  }

  return (
    <InvoicePaymentProvider onPaid={handlePaymentSuccess}>
      <div className="myAccountMainContainer">
        <Layout className="Invoice__Layout">
          <MyAccountSideMenu
            selectedMenuItem="4"
            handleMenuClick={handleMenuClick}
          />
          <main className="Invoice__Content">
            <Slide direction="left" triggerOnce>
              <div className="Invoice__Body">
                <header className="Invoice__Header">
                  <div>
                    <h1 className="Invoice__Title">All Invoices</h1>
                    <p className="Invoice__Description">
                      View all subscription, top-up, and submission invoices in one
                      place.
                      {allowCreateSubmissionInvoice && " You can also create custom invoices from selected submissions."}
                    </p>
                  </div>
                  {allowCreateSubmissionInvoice && (
                    <Button
                      type="primary"
                      className="Invoice__CreateButton"
                      icon={<ReceiptLineIcon aria-hidden />}
                      onClick={() => setCreatingInvoice(true)}
                    >
                      Create Submission Invoice
                    </Button>
                  )}
                </header>

                <div className="Invoice__Controls">
                  <div className="Invoice__ControlsLeft">
                    <Input
                      type="search"
                      size="large"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      onPressEnter={() => applySearch(search)}
                      suffix={(
                        <Button
                          type="text"
                          icon={<SearchLineIcon aria-hidden />}
                          className="Invoice__SearchButton"
                          aria-label="Search invoices"
                          onClick={() => applySearch(search)}
                        />
                      )}
                      placeholder="Search by invoice ID, invoice name, or type..."
                      allowClear
                      className="Invoice__Search"
                    />
                    <DatePicker
                      handleSearchDate={handleDateFilter}
                      value={dateSelected}
                      onChange={setDateSelected}
                      prevValue={prevDateSelected}
                      setPrevValue={setPrevDateSelected}
                      searchOnClear
                      popoverPlacement="bottomLeft"
                      datePickerDisplayProps={{
                        displayClassName: `Invoice__DatePicker${
                          dateSelected ? " Invoice__DatePicker--active" : ""
                        }`,
                        showText: true,
                        iconTooltipTitle: "Filter by date",
                        emptyPlaceholder: "Select date range",
                        customCalendarIcon: (
                          <CalendarLineIcon className="Invoice__DatePickerIcon" aria-hidden />
                        ),
                      }}
                    />
                    <Select
                      className="Invoice__CreatedBy"
                      popupMatchSelectWidth={240}
                      dropdownAlign={{ points: ["tc", "bc"], offset: [0, 4] }}
                      allowClear
                      placeholder="Created by"
                      value={createdBy || undefined}
                      options={creatorOptions}
                      {...getSearchableSelectProps()} // eslint-disable-line react/jsx-props-no-spreading
                      /* Keep ↓ after spread — AntD otherwise swaps to trailing search while open */
                      suffixIcon={selectSearchSuffixIcon}
                      classNames={selectPopupClassNames("Invoice__CreatedByDropdown")}
                      virtual={false}
                      listHeight={256}
                      popupRender={(menu) => renderSelectPopupScrollbar(menu, {
                        optionCount: (creatorOptions || []).length,
                      })}
                      optionRender={(option) => (
                        <HighlightText text={option.label} query={createdBySearch} />
                      )}
                      onSearch={setCreatedBySearch}
                      onBlur={() => setCreatedBySearch("")}
                      onChange={(value) => {
                        setCreatedBy(value || "");
                        setCreatedBySearch("");
                        resetAndReload();
                      }}
                    />
                  </div>

                  <div className="Invoice__TypeFilters" role="group" aria-label="Invoice type filters">
                    {INVOICE_TYPE_FILTERS.map((filter) => {
                      const isActive = activeTypes.includes(filter.key);

                      return (
                        <button
                          key={filter.key}
                          type="button"
                          className={[
                            "Invoice__TypeFilter",
                            isActive ? "Invoice__TypeFilter--selected" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          aria-pressed={isActive}
                          onClick={() => toggleTypeFilter(filter.key)}
                        >
                          <span
                            className={`Invoice__TypeFilterSwatch ${filter.swatchClassName}`}
                            aria-hidden
                          />
                          <span className="Invoice__TypeFilterLabel">{filter.label}</span>
                          {isActive && (
                            <span className="Invoice__TypeFilterClear" aria-hidden>
                              <CloseOutlined />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="Invoice__TableWrap">
                  <StyledScrollbar
                    ref={scrollbarRef}
                    className="Invoice__ScrollBar"
                    disableTrackYWidthCompensation
                    onScrollStop={handleScrollToBottom}
                  >
                    <Table
                      className="Invoice__Table"
                      rowKey="id"
                      rowClassName={(record) => (
                        createdByQuery
                        && record.createdBy?.toLowerCase().includes(createdByQuery.trim().toLowerCase())
                          ? "Invoice__Row--matched"
                          : ""
                      )}
                      columns={columns}
                      dataSource={records}
                      loading={loading}
                      pagination={false}
                    />
                    {loadingMore && (
                    <div className="Invoice__LoadMore">
                      <Loading />
                    </div>
                    )}
                  </StyledScrollbar>
                </div>
              </div>
            </Slide>
          </main>
        </Layout>
        <PdfPreviewModal
          open={Boolean(previewRecord)}
          title="Invoice"
          onClose={() => setPreviewRecord(null)}
          files={previewFiles}
          errorMessage="Unable to preview this invoice."
          width="40vw"
        />
      </div>
    </InvoicePaymentProvider>
  );
}
